// components/TimeTrackingHeatmap.jsx - USING REAL DATA
import React, { useState } from "react";
import "./TimeTrackingHeatmap.css";

const TimeTrackingHeatmap = ({ userActivity, dateRange = "thisWeek" }) => {
  const [selectedDay, setSelectedDay] = useState(null);

  // Use real data
  const heatmapData = Array.isArray(userActivity) ? userActivity : [];
  
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const dayKeys = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];

  const getHeatmapColor = (value) => {
    if (value >= 80) return "high-activity";
    if (value >= 60) return "medium-high";
    if (value >= 40) return "medium";
    if (value >= 20) return "medium-low";
    return "low";
  };

  const getHourCategory = (hour) => {
    if (!hour) return "off-hours";
    if (hour.includes("6 AM") || hour.includes("9 PM")) return "off-hours";
    if (hour.includes("9 AM") || hour.includes("12 PM") || hour.includes("3 PM")) return "core-hours";
    return "evening";
  };

  // Calculate insights from real data
  const calculateInsights = () => {
    if (heatmapData.length === 0) {
      return {
        avgActivity: 0,
        afterHoursPercent: 0,
        peakDays: 0
      };
    }
    
    let totalActivity = 0;
    let totalCells = 0;
    let afterHoursActivity = 0;
    let afterHoursCount = 0;
    const dayActivities = {};
    
    // Initialize day activities
    dayKeys.forEach(day => {
      dayActivities[day] = 0;
    });
    
    // Calculate totals
    heatmapData.forEach(row => {
      const isAfterHours = row.hour && (row.hour.includes("6 PM") || row.hour.includes("9 PM"));
      
      dayKeys.forEach(day => {
        const value = row[day] || 0;
        totalActivity += value;
        totalCells += 1;
        dayActivities[day] += value;
        
        if (isAfterHours) {
          afterHoursActivity += value;
          afterHoursCount += 1;
        }
      });
    });
    
    // Find peak days (days with above-average activity)
    const avgPerDay = totalActivity / dayKeys.length;
    const peakDays = dayKeys.filter(day => dayActivities[day] > avgPerDay).length;
    
    return {
      avgActivity: totalCells > 0 ? Math.round(totalActivity / totalCells) : 0,
      afterHoursPercent: afterHoursCount > 0 ? Math.round(afterHoursActivity / afterHoursCount) : 0,
      peakDays
    };
  };

  const insights = calculateInsights();

  return (
    <div className="time-tracking-heatmap">
      <div className="heatmap-header">
       
        
      </div>

      <div className="heatmap-container">
        <div className="hour-labels">
          {heatmapData.length > 0 ? (
            heatmapData.map((row, index) => (
              <div key={index} className={`hour-label ${getHourCategory(row.hour)}`}>
                {row.hour || `Slot ${index + 1}`}
              </div>
            ))
          ) : (
            // Show placeholders when no data
            ["6 AM", "9 AM", "12 PM", "3 PM", "6 PM", "9 PM"].map((hour, index) => (
              <div key={index} className="hour-label off-hours">
                {hour}
              </div>
            ))
          )}
        </div>

        <div className="heatmap-grid">
          {days.map((day, dayIndex) => (
            <div key={day} className="heatmap-column">
              <div className="day-header">{day.substring(0, 1)}</div>
              {heatmapData.length > 0 ? (
                heatmapData.map((row, hourIndex) => {
                  const dayKey = dayKeys[dayIndex];
                  const value = row[dayKey] || 0;
                  return (
                    <div
                      key={`${day}-${hourIndex}`}
                      className={`heatmap-cell ${getHeatmapColor(value)} ${selectedDay === day ? 'selected' : ''}`}
                      onMouseEnter={() => setSelectedDay(day)}
                      onMouseLeave={() => setSelectedDay(null)}
                      title={`${day} ${row.hour || ''}: ${value}% activity`}
                    >
                      <div className="cell-value">{value}%</div>
                    </div>
                  );
                })
              ) : (
                // Show empty cells when no data
                Array.from({ length: 6 }).map((_, hourIndex) => (
                  <div
                    key={`${day}-${hourIndex}`}
                    className="heatmap-cell low"
                    title="No data available"
                  >
                    <div className="cell-value">0%</div>
                  </div>
                ))
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="heatmap-summary">
        <div className="summary-stat">
          <div className="stat-value">{insights.avgActivity}%</div>
          <div className="stat-label">Avg Activity</div>
        </div>
        <div className="summary-stat">
          <div className="stat-value">{insights.afterHoursPercent}%</div>
          <div className="stat-label">After Hours</div>
        </div>
        <div className="summary-stat">
          <div className="stat-value">{insights.peakDays}</div>
          <div className="stat-label">Peak Days</div>
        </div>
      </div>
    </div>
  );
};

export default TimeTrackingHeatmap;